import { APP_ENVIRONMENT } from '@config/env';
import type React from 'react';
import { Component } from 'react';
import Bubble from '../containers/Bubble';
import ChatWindow from '../containers/ChatWindow';
import { ConfigContext } from '../context/ConfigContext';
import { getChatState, setChatState } from '../services/chatStateService';
import * as eventHandlerService from '../services/eventHandlerService';
import languageService from '../services/languageService';
import localStorageService from '../services/localStorageService';
import { sendUpdateWindowType } from '../services/postMessageService';
import querystringService from '../services/querystringService';
import { getMessagesOfUser } from '../services/userService';
import type { AppProps, AppState } from '../types/App';
import type {
  ChatActivityStatus,
  ChatHistory,
  Message,
  MessageDirection,
  MessagePayload,
} from '../types/ChatWindow';
import type { ChatWindowState, InitStateProps } from '../types/configs';
import Greeting from './Greeting';

class App extends Component<AppProps, AppState> {
  static contextType = ConfigContext;
  context!: React.ContextType<typeof ConfigContext>;
  registeredEvents = {
    set: () => this.setState({ registeredEvents: true }),
    get: () => {
      const { registeredEvents } = this.state;
      return registeredEvents;
    },
    delete: () => this.setState({ registeredEvents: false }),
  };

  constructor(props: AppProps) {
    super(props);
    const {
      config: {
        options: { defaultState = 'closed', allowTextInput },
      },
    } = props;

    const toggleState = false;
    const chatState = getChatState();
    const windowState = chatState === 'closed' ? chatState : defaultState;

    this.initState({
      allowTextInput,
      windowType: windowState,
      windowToggle: toggleState,
    });
  }

  fetchMessagesForId = async () => {
    const id = await this.getId();

    if (!id) {
      return;
    }

    await this.setMessages(id);
  };

  enableChat = async () => {
    this.setState({ chatEnabled: true });
    this.fetchMessagesForId();
  };

  componentDidMount = async () => {
    // Disable console logging in production and staging
    if (!['development', 'local'].includes(APP_ENVIRONMENT)) {
      console.log = () => {};
      console.debug = () => {};
      console.error = () => {};
      console.info = () => {};
    }

    querystringService.register(window.location.search);

    const { language } = this.context;
    languageService.registerLanguage(language);
    eventHandlerService.subscribeToEvent('new-webclient', () => {
      this.setState({ chatEnabled: false });
      this.registeredEvents.delete();
    });

    this.fetchMessagesForId();

    this.showWindowIfNeeded();
  };

  componentDidUpdate = (_prevProps: AppProps, prevState: AppState) => {
    const { messages } = this.state;

    if (prevState.messages.length !== messages.length && messages.length > 0) {
      this.setAllowTextInputOnMessage(messages);
    }
  };

  // * Check if the text input should be visible or not
  getTextInputStatus = (
    messagePayload: MessagePayload,
    defaultAllowTextInput = true,
  ) => {
    // * Disable text input on dropdown
    if (messagePayload.type === 'dropdown') {
      return false;
    }

    if (
      'options' in messagePayload &&
      typeof messagePayload?.options?.webclient?.buttonInteractionOnly ===
        'boolean'
    ) {
      // if the buttonInteractionOnly is set to true, we should not allow text input ( send back 'false' input status )
      return !messagePayload?.options?.webclient?.buttonInteractionOnly;
    }

    if (defaultAllowTextInput) {
      // DEPRECATED: since we introduced the new buttonInteractionOnly field, this value is coming from the main webclient config instead of the block config.
      return defaultAllowTextInput;
    }

    if (
      'options' in messagePayload &&
      typeof messagePayload?.options?.webclient?.allowTextInput === 'boolean' // DEPRECATED: since we introduced the new buttonInteractionOnly field, but because of the old configs, it has to be kept until they are rewritten.
    ) {
      return messagePayload?.options?.webclient?.allowTextInput;
    }

    return defaultAllowTextInput;
  };

  setAllowTextInputOnMessage = (messages: Message[]) => {
    const lastMessagePayload = messages?.[messages.length - 1]?.payload;

    const {
      config: {
        options: { allowTextInput: allowTextInputDefault },
      },
    } = this.props;

    this.setState((prevState) => ({
      ...prevState,
      allowTextInput: this.getTextInputStatus(
        lastMessagePayload,
        allowTextInputDefault,
      ),
    }));
  };

  getId = async () => {
    if (localStorageService.get('id')) {
      return localStorageService.get('id');
    }

    return null;
  };

  showWindowIfNeeded = () => {
    const { popupTimeout } = this.context;
    const { windowType } = this.state;

    if (windowType === 'closed') {
      return;
    }

    if (windowType === 'fullscreen') {
      this.showWindow();
      return;
    }

    setTimeout(() => {
      this.showWindow();
    }, popupTimeout);
  };

  showWindow = () => {
    const { windowType } = this.state;
    this.setState({ windowToggle: true });
    this.sendWindowStatus({
      windowToggle: true,
      windowType: windowType,
    });
  };

  // Get the status of the chat window
  getWindowStatus = ({ windowToggle, windowType }) => {
    if (!windowToggle) {
      return 'closed';
    }
    return windowType;
  };

  // Send window status to the parent window via postMessage
  sendWindowStatus = ({ windowToggle, windowType }) => {
    const windowStatus = this.getWindowStatus({ windowToggle, windowType });
    sendUpdateWindowType(windowStatus);
  };

  handleMinimizeButton = (show = false) => {
    const { windowType, windowTimeout, windowToggle } = this.state;

    if (windowTimeout) {
      clearTimeout(windowTimeout);
    }

    this.setState({
      windowTimeout: undefined,
    });

    if (windowType === 'closed') {
      this.setState({ windowType: 'open', windowToggle: true });
      this.sendWindowStatus({ windowToggle: true, windowType: 'open' });
      setChatState('open');
      return;
    }

    const windowToggleState = !windowToggle;

    setChatState(windowToggle ? 'closed' : 'open');
    this.setState({ windowToggle: windowToggleState });
    this.sendWindowStatus({
      windowToggle: windowToggleState,
      windowType,
    });
  };

  onLaterButtonClick = () => {
    const { windowType } = this.state;
    const { config } = this.props;
    const timeout = config.options.greetingTimeout || 60000;
    this.setState({ windowToggle: false });
    this.setState({
      windowTimeout: setTimeout(() => {
        this.setState({ windowToggle: true });
        this.sendWindowStatus({
          windowToggle: true,
          windowType,
        });
      }, timeout),
    });
    this.sendWindowStatus({
      windowToggle: false,
      windowType: windowType,
    });
  };

  hideGreeting = () => {
    this.setState({ windowType: 'open', windowToggle: true });
    this.sendWindowStatus({ windowToggle: true, windowType: 'open' });
  };

  addMessage = (direction: MessageDirection, params: MessagePayload) => {
    const { messages } = this.state;
    this.setState({
      messages: [...messages, { direction, payload: params }],
    });
  };

  setMessages = async (id: string) => {
    const { windowType } = this.state;
    const chatHistory = await getMessagesOfUser(id);
    const messages = this.convertChatHistoryToMessages(chatHistory);
    const chatState = getChatState();

    if (
      chatState !== 'closed' &&
      !['fullscreen', 'open'].includes(windowType)
    ) {
      this.setState({ windowType: 'open' });
    }
    this.setState({ messages });
  };

  changeWindowState = (state: ChatWindowState) => {
    this.setState({ windowType: state });
  };

  changeChatActivityStatus = (status: ChatActivityStatus) => {
    this.setState({ chatActivityStatus: status });
  };

  initState = (props: InitStateProps) => {
    const initialValues = {
      chatEnabled: true,
      allowTextInput: true, // DEPRECATED: since we introduced the new buttonInteractionOnly field, but because of the old configs, it has to be kept until they are rewritten.
      windowType: props.windowType,
      windowToggle: props.windowToggle,
      messages: [],
      registeredEvents: false,
      bubbleClickHandler: this.handleMinimizeButton,
      chatActivityStatus: 'online' as ChatActivityStatus,
    };

    // The state is not initialized yet, this is the first time the component is rendered
    if (!this.state) {
      this.state = initialValues;
      return;
    }

    // The state is already initialized, we need to update the re-initialized/reset values
    this.setState(initialValues);
  };

  isValidEntryBlockTypeByDirection = (direction: string, type: string) => {
    switch (direction) {
      case 'in': {
        return ['text', 'button', 'image'].indexOf(type) > -1;
      }
      case 'out': {
        return (
          [
            'text',
            'text-v2',
            'button',
            'textWithButtons',
            'imageWithButtons',
            'databaseUpdate',
            'imageCarousel',
            'feedback',
          ].indexOf(type) > -1
        );
      }
      case 'system': {
        return type === 'system';
      }
      default:
        return false;
    }
  };

  convertChatHistoryToMessages = (history: ChatHistory[]): Message[] => {
    // TODO: Move filter to backend
    const filteredHistory =
      history?.filter((entry) =>
        this.isValidEntryBlockTypeByDirection(
          entry.direction,
          entry.messageType,
        ),
      ) ?? [];

    const parsedHistory = filteredHistory
      ?.map((entry, index) => {
        return {
          direction: entry.direction,
          payload: this.determinePayload(entry, index, filteredHistory),
        };
      })
      // * Do one final filter in case the data is incorrect (payload is null)
      ?.filter((message): message is Message => Boolean(message.payload));

    return parsedHistory;
  };

  getTextFromButton = (data) => {
    if (data?.formattedValue) {
      return data.formattedValue;
    }
    if (data) {
      const { content } = data;
      if (typeof content === 'object') {
        if (content.title) {
          return content.title;
        }
      }
      return content;
    }
    return '';
  };

  determinePayload = (
    data: ChatHistory,
    index: number,
    allHistory: any[],
  ): MessagePayload | null => {
    const allHistoryLength = allHistory.length;

    switch (data.messageType) {
      case 'text': {
        return {
          type: 'text',
          data: {
            text: data.message.content,
            replaceTextUrl: data.message.replaceTextUrl,
          },
          options: data.message.options,
        };
      }
      case 'image': {
        return { type: 'image', data: { url: data.message.content.url } };
      }
      case 'textWithButtons': {
        // * Text with buttons turn into texts since we are using exclusively quickreplies
        return index + 1 === allHistoryLength
          ? {
              type: 'textWithButtons',
              data: {
                text: data.content.text,
                buttons: data.content.buttons,
                replaceTextUrl: data.content.replaceTextUrl,
              },
              options: data.options,
            }
          : {
              type: 'text',
              data: {
                text: data.content.text,
                replaceTextUrl: data.content.replaceTextUrl,
              },
            };
      }
      case 'text-v2': {
        // * in case the payload is incorrect send back null
        if (!data.content) {
          return null;
        }

        return data?.content?.buttons?.length > 0
          ? {
              type: 'textWithButtons',
              data: {
                text: data.content.text,
                buttons: data.content?.buttons ?? [],
                replaceTextUrl: data.content.replaceTextUrl,
                ...(data?.content?.llmUsageId && {
                  llmUsageId: data.content.llmUsageId,
                }),
              },
              options: data.options,
            }
          : {
              type: 'text',
              data: {
                text: data.content.text,
                replaceTextUrl: data.content.replaceTextUrl,
              },
              options: data?.options || {},
            };
      }
      case 'imageCarousel': {
        return {
          type: 'carousel',
          data: { ratio: 'square', elements: data.content },
          options: data?.options,
        };
      }
      case 'button': {
        return {
          type: 'text',
          data: {
            text: this.getTextFromButton(data),
          },
        };
      }
      case 'credential': {
        return { type: 'text', data: { text: 'Authenticated' } };
      }
      case 'feedback': {
        return {
          type: 'feedback',
          data: {
            submitText: data.content.submitText,
            minRating: data.content.minRating,
            maxRating: data.content.maxRating,
            rating: data.content.rating,
          },
        };
      }
      case 'system': {
        return {
          type: 'system',
          data: {
            text: data?.message?.content,
          },
        };
      }
      default: {
        throw Error('Invalid message type!');
      }
    }
  };

  render() {
    const {
      chatEnabled,
      allowTextInput,
      windowToggle,
      messages,
      bubbleClickHandler,
      windowType,
      chatActivityStatus,
    } = this.state;

    const { config } = this.props;

    return (
      <div className='main'>
        {windowToggle && ['open', 'fullscreen'].includes(windowType) && (
          <ChatWindow
            onClose={this.handleMinimizeButton}
            chatEnabled={chatEnabled}
            onEnableChat={this.enableChat}
            changeWindowState={this.changeWindowState}
            changeChatActivityStatus={this.changeChatActivityStatus}
            chatActivityStatus={chatActivityStatus}
            sendWindowStatus={this.sendWindowStatus}
            allowTextInput={allowTextInput}
            fullscreen={windowType === 'fullscreen'}
            messages={messages}
            addMessage={this.addMessage}
            registeredEvents={this.registeredEvents}
            setMessages={this.setMessages}
            initState={this.initState}
            data={config.data}
          />
        )}
        {windowToggle && windowType === 'greeting' && (
          <Greeting
            onClose={this.handleMinimizeButton}
            onButtonClick={this.hideGreeting}
            onLaterClick={this.onLaterButtonClick}
          />
        )}
        {windowType !== 'fullscreen' && (
          <Bubble
            changeChatActivityStatus={this.changeChatActivityStatus}
            clickHandler={bubbleClickHandler}
            windowType={windowType}
            windowToggle={windowToggle}
          />
        )}
      </div>
    );
  }
}

export default App;
